import { v4 as uuidv4 } from "uuid";
import { Binder, BinderDocument, BinderDocumentVersion, BinderFolder, BinderType } from "../models/binder";
import { ReferenceType } from "../models/document";

export const RESIDENT_BINDER_FOLDER_CODES = {
  RESIDENT_INFORMATION: "resident_information",
  ASSESSMENT: "assessment_preliminary_service_plan",
  NEGOTIATED_CARE_PLAN: "negotiated_care_plan",
  LEGAL_DOCUMENTS: "legal_documents",
  NURSE_DELEGATION: "nurse_delegation",
  MEDICATION_LOGS: "medication_logs",
} as const;

type BinderBundle = {
  binder: Binder | null;
  folders: BinderFolder[];
  documents: BinderDocument[];
};

export const ensureResidentBinderBundle = async (
  backendAPIStore: any,
  residentId: string,
): Promise<BinderBundle> => {
  if (typeof backendAPIStore?.init === "function") {
    await backendAPIStore.init();
  }

  const existing = await backendAPIStore.getBinderByReference(ReferenceType.RESIDENT, residentId);
  if (existing?.binder) {
    return {
      binder: existing.binder,
      folders: existing.folders || [],
      documents: existing.documents || [],
    };
  }

  const bootstrapped = await backendAPIStore.bootstrapBinder({
    binderType: BinderType.RESIDENT,
    referenceType: ReferenceType.RESIDENT,
    referenceId: residentId,
  });

  return {
    binder: bootstrapped?.binder || null,
    folders: bootstrapped?.folders || [],
    documents: [],
  };
};

export const ensureStaffBinderBundle = async (
  backendAPIStore: any,
  staffId: string,
): Promise<BinderBundle> => {
  const normalizedStaffId = String(staffId || "").trim();
  if (!normalizedStaffId) throw new Error("A staff account is required before credential files can be uploaded.");
  if (typeof backendAPIStore?.init === "function") await backendAPIStore.init();
  const existing = await backendAPIStore.getBinderByReference(ReferenceType.USER, normalizedStaffId);
  if (existing?.binder) return { binder: existing.binder, folders: existing.folders || [], documents: existing.documents || [] };
  const bootstrapped = await backendAPIStore.bootstrapBinder({ binderType: BinderType.STAFF, referenceType: ReferenceType.USER, referenceId: normalizedStaffId });
  return { binder: bootstrapped?.binder || null, folders: bootstrapped?.folders || [], documents: bootstrapped?.documents || [] };
};

export const getBinderFolderByCode = (
  folders: BinderFolder[],
  folderCode: string,
): BinderFolder | undefined => folders.find((folder) => folder.folderCode === folderCode);

export const sortBinderDocumentsForSelection = (documents: BinderDocument[]) => {
  return [...documents].sort((left, right) => {
    if (!!left.isPrimaryDocument !== !!right.isPrimaryDocument) {
      return left.isPrimaryDocument ? -1 : 1;
    }

    const leftModified = String((left as any).modifiedAt || left.createdAt || "");
    const rightModified = String((right as any).modifiedAt || right.createdAt || "");
    return rightModified.localeCompare(leftModified);
  });
};

export const getFolderDocuments = (
  documents: BinderDocument[],
  folderId?: string,
) => sortBinderDocumentsForSelection(documents.filter((document) => document.folderId === folderId));

export const getPrimaryDocument = (documents: BinderDocument[]) =>
  documents.find((document) => document.isPrimaryDocument) || documents[0] || null;

export const getLatestBinderDocumentVersion = async (
  backendAPIStore: any,
  binderDocumentId?: string,
): Promise<BinderDocumentVersion | null> => {
  if (!binderDocumentId) {
    return null;
  }
  const versions = await backendAPIStore.listBinderDocumentVersions(binderDocumentId);
  return versions?.[0] || null;
};

export const upsertBinderDocumentWithVersion = async (input: {
  backendAPIStore: any;
  binderId: string;
  folderId: string;
  folderName: string;
  referenceId: string;
  title: string;
  fileName: string;
  mimeType: string;
  existingDocuments?: BinderDocument[];
  file?: File;
  existingObjectKey?: string;
  isPrimaryDocument?: boolean;
}) => {
  const {
    backendAPIStore,
    binderId,
    folderId,
    folderName,
    referenceId,
    title,
    fileName,
    mimeType,
    existingDocuments = [],
    file,
    existingObjectKey,
    isPrimaryDocument,
  } = input;

  let binderDocument = existingDocuments.find((document) =>
    document.folderId === folderId && (document.title || "").trim().toLowerCase() === title.trim().toLowerCase()
  );

  if (!binderDocument?.binderDocumentId) {
    binderDocument = await backendAPIStore.createBinderDocument({
      binderId,
      folderId,
      title,
      documentCategory: folderName,
      isPrimaryDocument: !!isPrimaryDocument,
    } as BinderDocument);
  } else if (isPrimaryDocument !== undefined && binderDocument.isPrimaryDocument !== isPrimaryDocument) {
    binderDocument = await backendAPIStore.updateBinderDocument({
      ...binderDocument,
      binderId,
      folderId,
      documentCategory: folderName,
      isPrimaryDocument,
    } as BinderDocument);
  }

  if (!binderDocument?.binderDocumentId) {
    throw new Error("Unable to create or resolve the target binder document.");
  }

  let s3Key = existingObjectKey;
  if (!s3Key) {
    if (!file) {
      throw new Error("A file or existing object key is required to save a binder document.");
    }
    const extension = fileName.includes(".") ? fileName.split(".").pop()?.toLowerCase() : "";
    s3Key = `binder/${binderId}/${binderDocument.binderDocumentId}/${uuidv4()}${extension ? `.${extension}` : ""}`;
    const presignedUrl = await backendAPIStore.getPresignedUrlForUpload(referenceId, ReferenceType.RESIDENT, s3Key);
    await backendAPIStore.uploadFileToS3(presignedUrl, file);
  }

  await backendAPIStore.createBinderDocumentVersion({
    binderDocumentId: binderDocument.binderDocumentId,
    fileName,
    s3Key,
    mimeType,
  });

  return binderDocument;
};

export const uploadNewBinderDocument = async (input: {
  backendAPIStore: any;
  binderId: string;
  folderId: string;
  folderName: string;
  referenceId: string;
  title: string;
  file: File;
  isPrimaryDocument?: boolean;
}) => {
  const {
    backendAPIStore,
    binderId,
    folderId,
    folderName,
    referenceId,
    title,
    file,
    isPrimaryDocument,
  } = input;

  const createdDocument = await backendAPIStore.createBinderDocument({
    binderId,
    folderId,
    title,
    documentCategory: folderName,
    isPrimaryDocument: !!isPrimaryDocument,
  } as BinderDocument);

  if (!createdDocument?.binderDocumentId) {
    throw new Error("Unable to create the new binder document.");
  }

  const extension = file.name.includes(".") ? file.name.split(".").pop()?.toLowerCase() : "";
  const s3Key = `binder/${binderId}/${createdDocument.binderDocumentId}/${uuidv4()}${extension ? `.${extension}` : ""}`;
  const presignedUrl = await backendAPIStore.getPresignedUrlForUpload(referenceId, ReferenceType.RESIDENT, s3Key);
  await backendAPIStore.uploadFileToS3(presignedUrl, file);

  await backendAPIStore.createBinderDocumentVersion({
    binderDocumentId: createdDocument.binderDocumentId,
    fileName: file.name,
    s3Key,
    mimeType: file.type || "application/octet-stream",
  });

  return createdDocument;
};
